import React, { useState } from 'react';
import { toast } from 'sonner';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi } from '@/services/timesheetApi';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { canSubmitTimesheet } from './timesheetSubmitValidation';
import { getApprovalErrorMessage } from '@/utils/approvalErrorMessage';
import type { DataMode } from '@/contexts/DataSourceContext';
import type { Employee, ExtendedWorkingPattern, Timesheet, TimesheetEntry, WorkingPattern } from '@/types/timesheet';

export interface UseTimesheetSubmissionParams {
  screenMode: DataMode;
  entries: TimesheetEntry[];
  entriesRef: React.RefObject<TimesheetEntry[]>;
  currentTimesheet: Timesheet;
  currentEmployee: Employee;
  employees: Employee[];
  timesheets: Timesheet[];
  updateTimesheet: (timesheet: Timesheet) => void;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  effectiveWorkingPattern: WorkingPattern | ExtendedWorkingPattern | undefined;
  companyHasCostingIntegration: boolean;
  hasBlockingPolicyViolation: boolean;
  hasMissingDescriptions: boolean;
  isDaily: boolean;
  autoSaveTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  setLastSaved: React.Dispatch<React.SetStateAction<Date | null>>;
}

export interface UseTimesheetSubmissionResult {
  isSaving: boolean;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  saveTimesheet: () => Promise<void>;
  submitTimesheet: () => Promise<void>;
  confirmationDialogOpen: boolean;
  setConfirmationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  confirmationMessage: string;
  handleConfirmationAccept: () => Promise<void>;
  handleConfirmationDecline: () => Promise<void>;
}

/** Draft saving, submission (live + demo) and the pre-stage rule confirmation dialog. */
export function useTimesheetSubmission({
  screenMode,
  entries,
  entriesRef,
  currentTimesheet,
  currentEmployee,
  employees,
  timesheets,
  updateTimesheet,
  organizationSettings,
  effectiveWorkingPattern,
  companyHasCostingIntegration,
  hasBlockingPolicyViolation,
  hasMissingDescriptions,
  isDaily,
  autoSaveTimerRef,
  setLastSaved,
}: UseTimesheetSubmissionParams): UseTimesheetSubmissionResult {
  // Loading state to prevent double-click / multiple concurrent API requests
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-stage rule confirmation dialog state
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingConfirmTimesheetId, setPendingConfirmTimesheetId] = useState<number | null>(null);

  const saveTimesheet = async () => {
    if (isSaving || isSubmitting) return;

    // Note: a missing cost centre does NOT block saving — drafts are allowed to be
    // incomplete. Cost centre is only enforced on submit/resubmit (see below).

    // Validate role requirement on save
    if (organizationSettings?.showRole && organizationSettings?.requireRole) {
      const missingRoles = entries.filter(entry => !entry.roleId);
      if (missingRoles.length > 0) {
        toast.error('Cannot save timesheet', {
          description: 'All entries must have a role assigned. Please add roles to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    // Cancel any pending auto-save timer — manual save takes over
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    setIsSaving(true);
    try {
      const updated = {
        ...currentTimesheet,
        entries: entriesRef.current,  // use ref so we always save the very latest entries
        status: 'draft' as const
      };
      updateTimesheet(updated);
      setLastSaved(new Date());
    } finally {
      setIsSaving(false);
    }
  };

  const submitTimesheet = async () => {
    if (isSaving || isSubmitting) return;

    const valid = await canSubmitTimesheet({
      screenMode,
      entries,
      organizationSettings,
      effectiveWorkingPattern,
      companyHasCostingIntegration,
      hasBlockingPolicyViolation,
      hasMissingDescriptions,
      isDaily,
    });
    if (!valid) return;

    // Show loading toast - declared outside try block so it can be dismissed in catch
    const loadingToast = toast.loading('Submitting timesheet...');
    setIsSubmitting(true);

    try {
      if (screenMode === 'live') {
        // LIVE MODE: Use backend API to trigger workflow engine

        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };

        // Check if this is a new timesheet that needs to be created first
        const isNewTimesheet = !currentTimesheet.id || currentTimesheet.id === 0;
        let timesheetId = currentTimesheet.id;

        if (isNewTimesheet) {
          const createdTimesheet = await timesheetApi.createTimesheet({
            ...timesheetToSubmit,
            id: 0,
            status: 'draft',
          });
          timesheetId = createdTimesheet.id;
        } else {
          // Update existing timesheet with current entries before submitting
          await timesheetApi.updateTimesheet(timesheetId, {
            ...timesheetToSubmit,
            status: 'draft', // Keep as draft, submit endpoint will change status
          });
        }

        // Now call the submit endpoint to trigger the workflow engine
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        // If the backend signals that a pre-stage rule requires user confirmation,
        // show the Accept/Decline dialog and halt the submission flow.
        if (submitResponse.requiresConfirmation && submitResponse.confirmationMessage) {
          setPendingConfirmTimesheetId(timesheetId);
          setConfirmationMessage(submitResponse.confirmationMessage);
          setConfirmationDialogOpen(true);
          setIsSubmitting(false);
          return;
        }

        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId
              ? 'Workflow started successfully'
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet submitted for approval');
        }

        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }

        // Notify dashboard to refresh submission consistency stats
        window.dispatchEvent(new Event('timesheet-submitted'));

      } else {
        // DEMO MODE: Use client-side workflow simulation (existing behavior)

        // Import the submission service dynamically
        const { submitTimesheetWithWorkflow } = await import('@/services/timesheetSubmissionService');

        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };

        // Submit with full workflow integration (client-side)
        const result = await submitTimesheetWithWorkflow(
          timesheetToSubmit,
          currentEmployee,
          employees,
          timesheets
        );

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        if (result.success) {
          // Update the timesheet in context
          updateTimesheet(result.timesheet);

          // Show success message with details
          if (result.autoApproved) {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 5000,
            });
          } else {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 4000,
            });
          }

        } else {
          toast.error('Failed to submit timesheet');
        }
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error submitting timesheet:', error);
      toast.error('Failed to submit timesheet', { description: getApprovalErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handlers for the pre-stage rule confirmation dialog (Accept / Decline)
  const handleConfirmationAccept = async () => {
    if (pendingConfirmTimesheetId == null) return;
    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting timesheet...');
    try {
      const response = await approvalsApi.confirmSubmission(pendingConfirmTimesheetId, true);
      toast.dismiss(loadingToast);

      if (response.message) {
        toast.success(response.message, { duration: 4000 });
      } else {
        toast.success('Timesheet submitted for approval');
      }

      const updatedTimesheet = await timesheetApi.getTimesheetById(pendingConfirmTimesheetId);
      if (updatedTimesheet) {
        updateTimesheet(updatedTimesheet);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error confirming submission:', error);
      toast.error('Failed to submit timesheet', { description: getApprovalErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
      setConfirmationDialogOpen(false);
      setPendingConfirmTimesheetId(null);
      setConfirmationMessage('');
    }
  };

  const handleConfirmationDecline = async () => {
    if (pendingConfirmTimesheetId == null) return;
    try {
      await approvalsApi.confirmSubmission(pendingConfirmTimesheetId, false);
      toast.info('Submission cancelled');
    } catch (error) {
      console.error('Error declining submission:', error);
    } finally {
      setConfirmationDialogOpen(false);
      setPendingConfirmTimesheetId(null);
      setConfirmationMessage('');
    }
  };

  return {
    isSaving,
    isSubmitting,
    setIsSubmitting,
    saveTimesheet,
    submitTimesheet,
    confirmationDialogOpen,
    setConfirmationDialogOpen,
    confirmationMessage,
    handleConfirmationAccept,
    handleConfirmationDecline,
  };
}
